<template>
  <div class="grid gap-16">
    <div class="grid gap-4">
      <p class="text-[--raw-verydarkgrey]">{{ i18n(t.intro) }}</p>
      <p class="raw-body-3 text-[--raw-darkgrey]">
        {{ i18n(t.lastUpdate) }}
      </p>
    </div>

    <div class="grid gap-4">
      <MetalAccordion
        sticky
        :open="openSection === 'hero'"
        :group-open="openSection !== null"
        @toggle="(isOpen) => onToggle('hero', isOpen)"
      >
        <template #header>
          <span class="raw-cta-2 text-left">{{ i18n(t.sectionHero) }}</span>
        </template>
        <section
          v-for="claim in HERO_CLAIMS"
          :key="claim.statement.id"
          class="grid gap-8"
        >
          <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
          <h3 class="raw-heading-4">
            <InlineMarkdown :message="claim.statement" />
          </h3>
          <p
            v-for="note in claim.notes"
            :key="note.id"
            class="text-[--raw-verydarkgrey]"
          >
            <InlineMarkdown :message="note" />
          </p>
          <ReferenceList :references="claim.references" />
        </section>
      </MetalAccordion>

      <MetalAccordion
        sticky
        :open="openSection === 'stories'"
        :group-open="openSection !== null"
        @toggle="(isOpen) => onToggle('stories', isOpen)"
      >
        <template #header>
          <span class="raw-cta-2 text-left">{{ i18n(t.sectionStories) }}</span>
        </template>
        <section
          v-for="story in STORIES"
          :key="story.title.id"
          class="grid gap-8"
        >
          <h3 class="raw-heading-4">{{ i18n(story.title) }}</h3>
          <ReferenceList ordered :references="story.references" />
        </section>
      </MetalAccordion>

      <MetalAccordion
        sticky
        :open="openSection === 'method'"
        :group-open="openSection !== null"
        @toggle="(isOpen) => onToggle('method', isOpen)"
      >
        <template #header>
          <span class="raw-cta-2 text-left">{{ i18n(t.sectionMethod) }}</span>
        </template>
        <section
          v-for="(section, index) in METHOD"
          :key="section.title.id"
          class="grid gap-8"
        >
          <h3 class="raw-heading-4">
            {{ index + 1 }}. {{ i18n(section.title) }}
          </h3>
          <MethodBlocks :blocks="section.blocks" />
          <div
            v-for="score in section.scores"
            :key="score.title.id"
            class="grid gap-4"
          >
            <h4 class="raw-body-2">{{ i18n(score.title) }}</h4>
            <MethodBlocks :blocks="score.blocks" />
          </div>
        </section>
      </MetalAccordion>

      <MetalAccordion
        sticky
        :open="openSection === 'bibliography'"
        :group-open="openSection !== null"
        @toggle="(isOpen) => onToggle('bibliography', isOpen)"
      >
        <template #header>
          <span class="raw-cta-2 text-left">{{
            i18n(t.sectionBibliography)
          }}</span>
        </template>
        <SourcesList :sources="bibliography" :numbers="bibliographyNumbers" />
        <section class="grid gap-8">
          <h4 class="raw-body-2">
            {{ i18n(t.methodOnlyTitle) }}
          </h4>
          <ReferenceList :references="METHOD_SOURCE_ORDER" />
        </section>
      </MetalAccordion>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import InlineMarkdown from '~/scopes/campaign/components/UI/InlineMarkdown.vue'
import ReferenceList from '~/scopes/campaign/components/UI/ReferenceList.vue'
import MetalAccordion from '~/scopes/campaign/components/highlights/metal/MetalAccordion.vue'
import SourcesList from '~/scopes/campaign/components/highlights/metal/SourcesList.vue'
import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'
import {
  type Citation,
  METHOD_SOURCES,
  METHOD_SOURCE_ORDER,
  SOURCES,
  SOURCE_ORDER,
} from '~/scopes/campaign/data/sources'

import MethodBlocks from './MethodBlocks.vue'
import translations from './SourcesDoc.translations'
import type { HeroClaim, MethodSection, StorySources } from './SourcesDoc.types'

// The client's SOURCES tab in their four sections, as one single-open `MetalAccordion`
// group. Bodies stay flat: a nested accordion would match its open ancestor's state
// selectors and spring every entry open.
//
// §4's numbered list is authored in `data/sources.ts`, since every metal sheet cites it.
const i18n = useI18n()
const { trackClick } = useTracking()

const t = translations
const ZONE = 'footer-sources'

type SectionId = 'hero' | 'stories' | 'method' | 'bibliography'

/** Unnumbered, as the client prints them — inventing numbers would break the marker contract. */
const verbatim = (...labels: I18nDescriptor[]): readonly Citation[] =>
  labels.map((label) => ({ label }))

const HERO_CLAIMS: readonly HeroClaim[] = [
  {
    statement: t.heroClaim1,
    references: verbatim(t.refAdeme2022),
  },
  {
    statement: t.heroClaim2,
    notes: [t.heroClaim2Note],
    references: verbatim(t.refShiftProject2021),
  },
  {
    statement: t.heroClaim3,
    references: verbatim(t.refAdeme2022Footprint),
  },
  {
    statement: t.heroClaim4,
    notes: [t.heroClaim4Note],
    references: verbatim(t.refLifespanScenario),
  },
  {
    statement: t.heroClaim5,
    notes: [t.heroClaim5Note, t.heroClaim5Lead],
    references: verbatim(
      t.refUnicefDrc2022,
      t.refAmnesty2016,
      t.refKara2018,
      t.refNiarchos2026,
    ),
  },
  {
    statement: t.heroClaim6,
    notes: [t.heroClaim6Disclaimer],
    references: [
      // The only §1 entry the client annotates.
      { label: t.refPblSocial2025, note: t.refPblSocial2025Note },
      { label: t.refYaleMyanmar2025 },
      { label: t.refNolen2026 },
      { label: t.refReutersGreenland2025 },
      { label: t.refReutersUkraine2025 },
      { label: t.refReutersCongoRwanda2025 },
      { label: t.refRussellAustralia2025 },
      { label: t.refScheyderPentagon2025 },
      { label: t.refJacksonReprieve2025 },
    ],
  },
  {
    statement: t.heroClaim7,
    notes: [t.heroClaim7Note],
    references: verbatim(t.refScienceSoil2025),
  },
  {
    statement: t.heroClaim8,
    references: verbatim(t.refPblEnvironmental2024),
  },
]

// Three wires are shared with §1 verbatim; the other three the client bylines in §1 and
// attributes to "Reuters." here, so those are two strings each.
const STORIES: readonly StorySources[] = [
  {
    title: t.storyHuman,
    references: verbatim(
      t.refAmnesty2016,
      t.refGenocideWatch2025,
      t.refPblSocial2025,
      t.refUnicefDrc2022,
      t.refGuardianCobaltLawsuit2019,
      t.refJacobs2018,
      t.refGreene2018,
      t.refSunwoda,
      t.refStopCarcinogensCobalt,
      t.refOshaCobaltDust,
      t.refAppleRecycledCobalt2023,
    ),
  },
  {
    title: t.storyEnvironmental,
    references: verbatim(
      t.refGuardianWestPapua2016,
      t.refWwfFreeport,
      t.refUnepToxicTrail2023,
      t.refWwfGold2021,
      t.refZeng2025,
    ),
  },
  {
    title: t.storyGeopolitical,
    references: verbatim(
      t.refEuParliament2025,
      t.refReutersGreenland2025,
      t.refReutersUkraine2025,
      t.refIeaGcmo2026,
      t.refReutersCongoRwanda2025,
      t.refReutersAustralia2025,
      t.refReutersPentagon2025,
      t.refReutersReprieve2025,
    ),
  },
]

// §3 cites by spreading the entry, so its numbers and wording come from `SOURCES` and
// cannot drift from §4. The unnumbered entries come from `METHOD_SOURCES`.
const METHOD: readonly MethodSection[] = [
  {
    title: t.method1Title,
    blocks: [
      { type: 'prose', title: t.labelMeasures, text: t.method1Measures },
      {
        type: 'references',
        title: t.labelPrimarySources,
        references: [
          { ...SOURCES.usgsMcs2025 },
          {
            ...METHOD_SOURCES.bgsWorldMineralProduction,
            note: t.method1RoleBgs,
          },
        ],
      },
      {
        type: 'prose',
        title: t.labelCalculation,
        text: t.method1Calculation,
      },
      {
        type: 'list',
        title: t.labelHowToRead,
        items: [{ label: t.method1HowToRead1 }, { label: t.method1HowToRead2 }],
      },
    ],
  },
  {
    title: t.method2Title,
    blocks: [
      { type: 'prose', title: t.labelMeasures, text: t.method2Measures },
      {
        type: 'references',
        title: t.labelPrimarySources,
        references: [
          { ...SOURCES.gomez2023, note: t.method2RoleGomez },
          {
            ...SOURCES.brunningSmartphone2023,
            note: t.method2RoleBrunning,
          },
        ],
      },
      {
        type: 'prose',
        title: t.labelCalculation,
        text: t.method2Calculation,
      },
      {
        type: 'prose',
        title: t.labelLimitations,
        text: t.method2Limitations,
      },
    ],
  },
  {
    title: t.method3Title,
    blocks: [
      { type: 'prose', title: t.labelWhatItIs, text: t.method3WhatItIs },
      {
        type: 'references',
        title: t.labelPrimarySources,
        references: [
          { ...SOURCES.gomez2023, note: t.method3RoleGomez },
          { ...SOURCES.brunningSmartphone2023 },
        ],
      },
      {
        type: 'list',
        title: t.labelDefinedScoring,
        items: [
          { label: t.method3Scoring1 },
          { label: t.method3Scoring2 },
          { label: t.method3Scoring3 },
          { label: t.method3Scoring4 },
          { label: t.method3Scoring5 },
        ],
      },
      {
        type: 'prose',
        title: t.labelLimitations,
        text: t.method3Limitations,
      },
    ],
  },
  {
    title: t.method4Title,
    blocks: [
      {
        type: 'prose',
        title: t.labelMetalList,
        text: t.method4MetalList,
      },
      { type: 'prose', text: t.method4Intro },
    ],
    scores: [
      {
        title: t.method4EnvTitle,
        blocks: [
          {
            type: 'prose',
            title: t.labelMeasures,
            text: t.method4EnvMeasures,
          },
          {
            type: 'list',
            title: t.labelScale,
            items: [
              { label: t.method4EnvScale1 },
              { label: t.method4EnvScale5 },
            ],
          },
          {
            type: 'references',
            title: t.labelPrimarySource,
            references: [{ ...SOURCES.ademe2024 }],
          },
          {
            type: 'prose',
            title: t.labelCalculation,
            text: t.method4EnvCalculation,
          },
        ],
      },
      {
        title: t.method4HumanTitle,
        blocks: [
          {
            type: 'prose',
            title: t.labelMeasures,
            text: t.method4HumanMeasures,
          },
          {
            type: 'list',
            title: t.labelScale,
            items: [
              { label: t.method4HumanScale1 },
              { label: t.method4HumanScale5 },
            ],
          },
          {
            type: 'references',
            title: t.labelPrimarySource,
            references: [{ ...SOURCES.ademe2024 }],
          },
          // The client repeats the environmental sentence here and under
          // geopolitical — one key, three references.
          {
            type: 'prose',
            title: t.labelCalculation,
            text: t.method4EnvCalculation,
          },
        ],
      },
      {
        title: t.method4GeoTitle,
        blocks: [
          {
            type: 'prose',
            title: t.labelMeasures,
            text: t.method4GeoMeasures,
          },
          {
            type: 'prose',
            title: t.labelCriticalNote,
            text: t.method4GeoCriticalNote,
          },
          {
            type: 'list',
            title: t.labelScale,
            items: [
              { label: t.method4GeoScale1 },
              { label: t.method4GeoScale5 },
            ],
          },
          {
            type: 'references',
            title: t.labelPrimarySource,
            references: [{ ...SOURCES.ademe2024 }],
          },
          {
            type: 'prose',
            title: t.labelCalculation,
            text: t.method4EnvCalculation,
          },
        ],
      },
      {
        title: t.method4SupplyTitle,
        blocks: [
          {
            type: 'prose',
            title: t.labelMeasures,
            text: t.method4SupplyMeasures,
          },
          {
            type: 'prose',
            title: t.labelRiskLevels,
            text: t.method4SupplyRiskLevels,
          },
          {
            type: 'references',
            title: t.labelPrimarySources,
            references: [
              {
                ...SOURCES.ieaGcmo2025,
                note: t.method4SupplyRoleIea,
              },
              {
                ...SOURCES.ademe2024,
                note: t.method4SupplyRoleAdeme,
              },
              // The client calls this [7], but their §4 gives [7] to the
              // IEA explorer — so it reads 30. Flagged, not reconciled.
              {
                ...SOURCES.greenIt,
                note: t.method4SupplyRoleGreenIt,
              },
              {
                ...SOURCES.usgsMcs2025,
                note: t.method4SupplyRoleUsgs,
              },
            ],
          },
          {
            type: 'prose',
            title: t.labelCalculation,
            text: t.method4SupplyCalculation,
          },
        ],
      },
    ],
  },
  {
    title: t.method5Title,
    blocks: [
      { type: 'prose', text: t.method5Intro },
      {
        type: 'prose',
        title: t.labelMetalList,
        text: t.method5MetalList,
      },
    ],
    scores: [
      {
        title: t.method5EnvTitle,
        blocks: [
          {
            type: 'list',
            ordered: true,
            items: [
              { label: t.method5EnvStep1 },
              { label: t.method5EnvStep2 },
              { label: t.method5EnvStep3 },
            ],
          },
          {
            type: 'list',
            items: [
              { label: t.method5EnvT1, score: 1 },
              { label: t.method5EnvT2, score: 2 },
              { label: t.method5EnvT3, score: 3 },
              { label: t.method5EnvT4, score: 4 },
              { label: t.method5EnvT5, score: 5 },
            ],
          },
          {
            type: 'references',
            title: t.labelPrimarySource,
            references: [{ ...METHOD_SOURCES.graedel2015 }],
          },
          { type: 'prose', text: t.method5EnvGraphiteException },
          {
            type: 'references',
            references: verbatim(t.refEngelsGraphite2022),
          },
        ],
      },
      {
        title: t.method5HumanTitle,
        blocks: [
          {
            type: 'list',
            ordered: true,
            items: [
              { label: t.method5HumanStep1 },
              { label: t.method5HumanStep2 },
              { label: t.method5HumanStep3 },
            ],
          },
          {
            type: 'list',
            items: [
              { label: t.method5HumanT1, score: 5 },
              { label: t.method5HumanT2, score: 4 },
              { label: t.method5HumanT3, score: 3 },
              { label: t.method5HumanT4, score: 2 },
              { label: t.method5HumanT5, score: 1 },
            ],
          },
          {
            type: 'references',
            title: t.labelPrimarySource,
            references: [{ ...METHOD_SOURCES.undpHdi }],
          },
        ],
      },
      {
        title: t.method5GeoTitle,
        blocks: [
          { type: 'prose', text: t.method5GeoIntro },
          {
            type: 'list',
            ordered: true,
            items: [
              { label: t.method5GeoStep1 },
              { label: t.method5GeoStep2 },
              { label: t.method5GeoStep3 },
              { label: t.method5GeoStep4 },
            ],
          },
          {
            type: 'list',
            items: [
              { label: t.method5GeoT1, score: 5 },
              { label: t.method5GeoT2, score: 4 },
              { label: t.method5GeoT3, score: 3 },
              { label: t.method5GeoT4, score: 2 },
              { label: t.method5GeoT5, score: 1 },
            ],
          },
          {
            type: 'references',
            title: t.labelPrimarySource,
            references: [{ ...METHOD_SOURCES.worldBankWgi }],
          },
        ],
      },
      {
        title: t.method5SupplyTitle,
        blocks: [
          {
            type: 'list',
            ordered: true,
            items: [
              { label: t.method5SupplyStep1 },
              { label: t.method5SupplyStep2 },
              { label: t.method5SupplyStep3 },
            ],
          },
          {
            type: 'list',
            items: [
              { label: t.method5SupplyT1, score: 1 },
              { label: t.method5SupplyT2, score: 2 },
              { label: t.method5SupplyT3, score: 3 },
              { label: t.method5SupplyT4, score: 4 },
              { label: t.method5SupplyT5, score: 5 },
            ],
          },
        ],
      },
    ],
  },
  {
    title: t.method6Title,
    blocks: [
      { type: 'prose', title: t.labelMeasures, text: t.method6Measures },
      {
        type: 'references',
        title: t.labelPrimarySources,
        references: [
          {
            ...SOURCES.ieaRecycling2024,
            note: t.method6RoleIea,
          },
          { ...SOURCES.jrcCircular2017, note: t.method6RoleJrc },
          { ...SOURCES.ademe2024, note: t.method6RoleAdeme },
        ],
      },
      {
        type: 'references',
        title: t.labelSecondarySource,
        references: [
          {
            ...SOURCES.unepRecyclingRates2011,
            note: t.method6RoleUnep,
          },
        ],
      },
      {
        type: 'list',
        title: t.labelThresholds,
        items: [
          { label: t.method6T5, score: 5 },
          { label: t.method6T4, score: 4 },
          { label: t.method6T3, score: 3 },
          { label: t.method6T2, score: 2 },
          { label: t.method6T1, score: 1 },
          { label: t.method6TNa },
        ],
      },
      {
        type: 'prose',
        title: t.labelLimitation,
        text: t.method6Limitation,
      },
    ],
  },
]

// The client's own numbers: renumbering by position would break every marker pointing here
// the day an entry is retired.
const bibliography = SOURCE_ORDER.map((key) => SOURCES[key].label)
const bibliographyNumbers = SOURCE_ORDER.map((key) => SOURCES[key].number)

const openSection = ref<SectionId | null>(null)

function onToggle(id: SectionId, isOpen: boolean) {
  openSection.value = isOpen ? id : null
  trackClick({ zone: ZONE, name: `section-${id}`, campaign: 'raw-materials' })
}
</script>
